import { useState, useEffect } from 'react'
import { Box, Typography, Divider } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { format, subDays, startOfDay, endOfDay } from 'date-fns'
import MUIDateTimePicker from '../../MUIDateTimePicker'
import { MUIButton } from '../../Shared'
import { CustomTooltip } from '../../Shared/CustomToolTip/CustomToolTip'

export type QuickRange = 'today' | 'last7days' | 'last30days' | 'custom'

export interface DateRangeValue {
  fromDate: string | null
  toDate: string | null
  quickRange: QuickRange | null
}

interface DateRangeFilterProps {
  value: DateRangeValue | null
  onApply: (value: DateRangeValue | null) => void
  onReset: () => void
}

const QUICK_RANGES: { key: QuickRange; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'last7days', label: 'Last 7 days' },
  { key: 'last30days', label: 'Last 30 days' },
  { key: 'custom', label: 'Custom' },
]

const toDateStr = (date: Date): string => format(date, 'yyyy-MM-dd')

const getQuickRangeDates = (range: QuickRange): { from: Date; to: Date } | null => {
  const today = new Date()
  if (range === 'today') return { from: startOfDay(today), to: endOfDay(today) }
  if (range === 'last7days') return { from: startOfDay(subDays(today, 6)), to: endOfDay(today) }
  if (range === 'last30days') return { from: startOfDay(subDays(today, 29)), to: endOfDay(today) }
  return null
}

const DateRangeFilter = ({ value, onApply, onReset }: DateRangeFilterProps) => {
  const [selectedRange, setSelectedRange] = useState<QuickRange | null>(value?.quickRange ?? null)
  const [fromDate, setFromDate] = useState<Date | null>(null)
  const [toDate, setToDate] = useState<Date | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, { message: string } | undefined>>(
    {},
  )

  useEffect(() => {
    if (!value) {
      setSelectedRange(null)
      setFromDate(null)
      setToDate(null)
      setFieldErrors({})
    } else {
      setSelectedRange(value.quickRange)
      if (value.fromDate) {
        const [year, month, day] = value.fromDate.split('-').map(Number)
        setFromDate(new Date(year, month - 1, day, 12, 0, 0))
      } else {
        setFromDate(null)
      }
      if (value.toDate) {
        const [year, month, day] = value.toDate.split('-').map(Number)
        setToDate(new Date(year, month - 1, day, 12, 0, 0))
      } else {
        setToDate(null)
      }
    }
  }, [value])

  const handleQuickRange = (key: QuickRange) => {
    setSelectedRange(key)
    setFieldErrors({})
    if (key !== 'custom') {
      const dates = getQuickRangeDates(key)
      if (dates) {
        setFromDate(dates.from)
        setToDate(dates.to)
      }
    } else {
      setFromDate(null)
      setToDate(null)
    }
  }

  const isFutureDate = (date: Date | null): boolean => {
    if (!date) return false
    const today = startOfDay(new Date())
    return startOfDay(date) > today
  }

  const handleFromDateChange = (date: Date | null) => {
    if (isFutureDate(date)) {
      setFromDate(null)
      setFieldErrors((prev) => ({
        ...prev,
        fromDate: { message: 'From date cannot be a future date' },
      }))
      return
    }
    setFromDate(date)
    setSelectedRange('custom')
    setFieldErrors((prev) => {
      const next = { ...prev }
      delete next['fromDate']
      if (next['toDate']?.message === 'To date must be after from date') delete next['toDate']
      return next
    })
  }

  const handleToDateChange = (date: Date | null) => {
    if (isFutureDate(date)) {
      setToDate(null)
      setFieldErrors((prev) => ({
        ...prev,
        toDate: { message: 'To date cannot be a future date' },
      }))
      return
    }
    setToDate(date)
    setSelectedRange('custom')
    setFieldErrors((prev) => {
      const next = { ...prev }
      delete next['toDate']
      return next
    })
  }

  const validateDates = (): boolean => {
    const errors: Record<string, { message: string }> = {}
    if (!fromDate) errors['fromDate'] = { message: 'From date is required' }
    if (!toDate) errors['toDate'] = { message: 'To date is required' }
    if (fromDate && toDate && fromDate > toDate) {
      errors['toDate'] = { message: 'To date must be after from date' }
    }
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const isApplyDisabled = !selectedRange || (selectedRange === 'custom' && (!fromDate || !toDate))

  const handleApply = () => {
    if (selectedRange === 'custom') {
      if (!validateDates()) return
      onApply({
        fromDate: fromDate ? toDateStr(fromDate) : null,
        toDate: toDate ? toDateStr(toDate) : null,
        quickRange: 'custom',
      })
      return
    }
    if (selectedRange) {
      const dates = getQuickRangeDates(selectedRange)
      if (dates) {
        onApply({
          fromDate: toDateStr(dates.from),
          toDate: toDateStr(dates.to),
          quickRange: selectedRange,
        })
      }
      return
    }
    onApply(null)
  }

  const handleReset = () => {
    setSelectedRange(null)
    setFromDate(null)
    setToDate(null)
    setFieldErrors({})
    onReset()
  }

  return (
    <Box sx={{ width: 480, padding: '20px 24px 16px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#1a1a1a' }}>
          Filter by Date
        </Typography>
        <CustomTooltip
          title='Filter notifications by a date range. Quick ranges auto-fill FROM and TO dates. Use Custom to pick specific dates.'
          arrow
          placement='left'
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: '#402080',
                fontSize: '12px',
                maxWidth: 220,
                '& .MuiTooltip-arrow': { color: '#402080' },
              },
            },
          }}
        >
          <InfoOutlinedIcon sx={{ color: '#71717a', fontSize: 20, cursor: 'pointer' }} />
        </CustomTooltip>
      </Box>

      {/* Quick ranges label */}
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          color: '#71717a',
          letterSpacing: '0.08em',
          mb: 1,
        }}
      >
        QUICK RANGES
      </Typography>

      {/* Quick range chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {QUICK_RANGES.map(({ key, label }) => {
          const isActive = selectedRange === key
          return (
            <MUIButton
              key={key}
              type='button'
              label={label}
              variant={isActive ? 'contained' : 'outlined'}
              handleClickCB={() => handleQuickRange(key)}
              nonDisableProp
              width='auto'
              height='36px'
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                px: 2,
                ...(isActive
                  ? {
                      backgroundColor: '#402080',
                      color: '#fff',
                      border: '1px solid #402080',
                      '&:hover': { backgroundColor: '#31196a' },
                    }
                  : {
                      backgroundColor: '#fff',
                      color: '#333',
                      border: '1px solid #d4d4d8',
                      '&:hover': { backgroundColor: '#f5f3ff', border: '1px solid #402080' },
                    }),
              }}
            />
          )
        })}
      </Box>

      {/* Date pickers — always visible so the user can type or pick dates freely.
          Switching to Custom chip resets both; editing a field auto-selects Custom. */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <MUIDateTimePicker
            label='FROM'
            type='date'
            name='fromDate'
            value={fromDate}
            onChange={handleFromDateChange}
            error={fieldErrors}
            required
            disableFuture
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <MUIDateTimePicker
            label='TO'
            type='date'
            name='toDate'
            value={toDate}
            onChange={handleToDateChange}
            error={fieldErrors}
            required
            disableFuture
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Footer */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <MUIButton
          type='button'
          label='Reset'
          variant='text'
          handleClickCB={handleReset}
          nonDisableProp
          width='auto'
          sx={{
            color: '#333',
            '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
          }}
        />
        <MUIButton
          type='button'
          label='Apply Filter'
          variant='contained'
          handleClickCB={handleApply}
          disabled={isApplyDisabled}
          nonDisableProp={!isApplyDisabled}
          width='auto'
          sx={{
            backgroundColor: '#402080',
            borderRadius: '8px',
            px: 3,
            '&:hover': { backgroundColor: '#31196a' },
            '&.Mui-disabled': { backgroundColor: '#c4b5e0', color: '#fff' },
          }}
        />
      </Box>
    </Box>
  )
}

export default DateRangeFilter
